import {
	Component, ContentChild, Input, Output, EventEmitter, OnChanges, OnDestroy, OnInit, SimpleChanges, forwardRef,
	TemplateRef, ViewChild, ViewEncapsulation,
} from '@angular/core';
import { FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type SelectOption = Record<string, unknown>;

/**
 * In-memory searchable single-select wrapping `mat-select` + `ngx-mat-select-search`.
 *
 * Usage:
 *   <app-searchable-select
 *     label="Product Group"
 *     [options]="form_options?.products || []"
 *     valueKey="product_id"
 *     labelKey="name"
 *     formControlName="product_id">
 *   </app-searchable-select>
 */
@Component({
	selector: 'app-searchable-select',
	template: `
		<mat-form-field appearance="outline" class="w-100" [subscriptSizing]="subscript_sizing">
			<mat-label>{{ label }}</mat-label>
			<mat-select #matSelect [formControl]="valueControl" [required]="required"
			            [multiple]="multiple"
			            [panelWidth]="panel_width"
			            [panelClass]="panel_class ? 'searchable-select-panel ' + panel_class : 'searchable-select-panel'"
			            (closed)="onSelectClosed()">
				<mat-select-trigger>{{ _triggerLabel() }}</mat-select-trigger>
				<mat-option class="searchable-select-search-option">
					<ngx-mat-select-search
						[formControl]="searchControl"
						placeholderLabel="{{ 'Search' | tt }}"
						noEntriesFoundLabel="{{ 'No matches' | tt }}">
					</ngx-mat-select-search>
				</mat-option>
				<mat-option *ngIf="allow_null" [value]="null" tt>None</mat-option>
				<ng-container *ngIf="!groupKey">
					<ng-container *ngFor="let opt of filteredOptions | async"
					              [ngTemplateOutlet]="optionRow" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
				</ng-container>
				<mat-optgroup *ngFor="let group of filteredGroups | async" [label]="group.label || ('Other' | tt)">
					<ng-container *ngFor="let opt of group.options"
					              [ngTemplateOutlet]="optionRow" [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
				</mat-optgroup>
				<ng-template #optionRow let-opt>
					<mat-option [value]="opt[valueKey]"
					            [disabled]="_isDisabled(opt)"
					            [class.searchable-select-multiline]="optionTemplate">
						<ng-container *ngIf="optionTemplate; else defaultLabel"
						              [ngTemplateOutlet]="optionTemplate"
						              [ngTemplateOutletContext]="{ $implicit: opt }"></ng-container>
						<ng-template #defaultLabel>{{ _formatLabel(opt) }}</ng-template>
					</mat-option>
				</ng-template>
			</mat-select>
		</mat-form-field>
	`,
	styles: [`
		.searchable-select-panel .searchable-select-search-option.mat-mdc-option {
			background: #fff;
		}
		.searchable-select-panel .mat-mdc-option.searchable-select-multiline {
			height: auto;
			min-height: 48px;
		}
	`],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SearchableSelectComponent),
			multi: true,
		},
	],
})
export class SearchableSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
	@ViewChild('matSelect') private _mat_select: MatSelect;

	@Input() label = '';
	@Input() options: any[] = [];
	@Input() valueKey = 'id';
	@Input() labelKey: string | string[] = 'name';
	@Input() allow_null = false;
	@Input() required = false;
	@Input() multiple = false;
	@Input() panel_width: string | number | null = null;
	@Input() panel_class = '';
	/** Option property that disables the option when explicitly `false` (e.g. `is_active`). */
	@Input() disabledKey?: string;
	/** Option property to group the open list by; groups keep the order in which `options` first mentions them. */
	@Input() groupKey?: string;
	/** Also match the search against the `groupKey` value, so typing a group name lists the whole group. */
	@Input() search_group_label = false;
	/** Custom formatter for an option's displayed label. Overrides `labelKey` for display only —
	 *  search still matches against `labelKey`, so price/detail text in the label won't pollute it. */
	@Input() optionLabelFn?: (opt: any) => string;
	/** Match Angular Material's `mat-form-field` subscriptSizing input — pass
	 *  "dynamic" to drop the reserved 22px for subscript text when aligning
	 *  with adjacent non-form-field controls (e.g. an icon button). */
	@Input() subscript_sizing: 'fixed' | 'dynamic' = 'fixed';
	@Output() selectionChange = new EventEmitter<any>();

	/** Optional caller-supplied option content, projected as `<ng-template #optionTemplate let-opt>`.
	 *  Renders custom (e.g. multi-line) HTML per option; the closed field still shows the plain
	 *  `_formatLabel` value via `mat-select-trigger`. Falls back to `_formatLabel` when absent. */
	@ContentChild('optionTemplate') optionTemplate?: TemplateRef<{ $implicit: any }>;

	valueControl = new FormControl<any>(null);
	searchControl = new FormControl<string>('');
	filteredOptions = new ReplaySubject<SelectOption[]>(1);
	filteredGroups = new ReplaySubject<{ label: string, options: SelectOption[] }[]>(1);

	private _onDestroy = new Subject<void>();
	private _onChange: (value: any) => void = () => {};
	private _onTouched: () => void = () => {};

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['options'] || changes['groupKey']) {
			this._applyFilter();
		}
	}

	ngOnInit(): void {
		this._applyFilter();

		this.searchControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
			this._applyFilter();
		});

		this.valueControl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(v => {
			this._onChange(v);
			this.selectionChange.emit(v);
		});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	open(): void {
		this._mat_select?.open();
	}

	onSelectClosed(): void {
		this._onTouched();
	}

	writeValue(v: any): void {
		this.valueControl.setValue(v, { emitEvent: false });
	}

	registerOnChange(fn: any): void {
		this._onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this._onTouched = fn;
	}

	setDisabledState(disabled: boolean): void {
		disabled
			? this.valueControl.disable({ emitEvent: false })
			: this.valueControl.enable({ emitEvent: false });
	}

	_isDisabled(opt: SelectOption): boolean {
		return !!this.disabledKey && opt[this.disabledKey] === false;
	}

	private _typedOptions(): SelectOption[] {
		return (this.options || []) as SelectOption[];
	}

	/** Plain single-line label for the closed field, looked up in the full (unfiltered) option list. */
	_triggerLabel(): string {
		const value = this.valueControl.value;
		if (value === null || value === undefined) return '';

		if (this.multiple && Array.isArray(value)) {
			return value
				.map(v => this._typedOptions().find(o => o[this.valueKey] === v))
				.filter(o => !!o)
				.map(o => this._formatLabel(o))
				.join(', ');
		}

		const opt = this._typedOptions().find(o => o[this.valueKey] === value);

		return opt ? this._formatLabel(opt) : '';
	}

	_formatLabel(opt: SelectOption): string {
		if (this.optionLabelFn) return this.optionLabelFn(opt);

		const keys = Array.isArray(this.labelKey) ? this.labelKey : [this.labelKey];
		return keys
			.map(k => opt[k])
			.filter(x => x !== undefined && x !== null)
			.join(', ');
	}

	private _applyFilter(): void {
		const search = (this.searchControl.value || '').toString().toLowerCase().trim();
		const keys = Array.isArray(this.labelKey) ? [...this.labelKey] : [this.labelKey];
		if (this.search_group_label && this.groupKey) keys.push(this.groupKey);

		const filtered = this._typedOptions().filter(opt =>
			!search || keys.some(k => String(opt[k] ?? '').toLowerCase().includes(search))
		);
		this.filteredOptions.next(filtered);
		this.filteredGroups.next(this.groupKey ? this._groupOptions(filtered) : []);
	}

	private _groupOptions(options: SelectOption[]): { label: string, options: SelectOption[] }[] {
		const groups = new Map<string, SelectOption[]>();
		for (const opt of options) {
			const label = String(opt[this.groupKey] ?? '');
			if (!groups.has(label)) groups.set(label, []);
			groups.get(label).push(opt);
		}

		return Array.from(groups, ([label, group_options]) => ({ label, options: group_options }));
	}
}
